/**
 * AccountAccessModal — one account's retailer access, tree by tree (Trusscore admin).
 *
 * Shows each retailer tree the person is on with a plain summary and an Edit
 * button that opens the tree access editor (ticks over retailer / sub-retailer /
 * locations), plus "Add to a retailer…". Also the job title used on estimates
 * they prepare. Opened from /admin/accounts.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, AlertTriangle, ShieldCheck, Pencil } from 'lucide-react';
import { AccessEditorModal, ACCESS_ROLE_LABEL } from '../../components/admin/AccessEditorModal';
import { fetchAccountAccess, setAccountProfile, type AccountAccess, type AccessMembership } from '../../services/adminAccountsApi';

interface Props {
  userId: number;
  onClose: () => void;
  /** Called after any change so the page behind can refresh. */
  onChanged?: () => void;
}

export const AccountAccessModal: React.FC<Props> = ({ userId, onClose, onChanged }) => {
  const [data, setData] = useState<AccountAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [jobTitle, setJobTitle] = useState('');
  const [editTree, setEditTree] = useState<number | null>(null);
  const [addId, setAddId] = useState('');
  useEffect(() => { if (data) setJobTitle(data.account.jobTitle ?? ''); }, [data]);

  const load = useCallback(async () => {
    try { setData(await fetchAccountAccess(userId)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }, [userId]);
  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy && editTree === null) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy, editTree]);

  const name = data ? ([data.account.firstName, data.account.lastName].filter(Boolean).join(' ') || data.account.email) : '';
  const byId = useMemo(() => new Map((data?.retailers ?? []).map(r => [r.id, r])), [data]);
  const rootOf = (id: number) => byId.get(id)?.parentRetailerId ?? id;

  // Group memberships by tree root.
  const trees = useMemo(() => {
    const out = new Map<number, AccessMembership[]>();
    for (const m of data?.memberships ?? []) { const r = rootOf(m.retailerId); out.set(r, [...(out.get(r) ?? []), m]); }
    return out;
    // eslint-disable-next-line
  }, [data]);

  const describe = (rows: AccessMembership[], rootId: number): string => {
    const root = byId.get(rootId)?.name ?? `#${rootId}`;
    const parts: string[] = [];
    for (const m of rows) {
      const n = byId.get(m.retailerId)?.name ?? `#${m.retailerId}`;
      if (m.status !== 'approved') { parts.push(`${n}: ${m.status}`); continue; }
      if (m.allLocations) parts.push(m.retailerId === rootId ? (m.includeChildren ? `entire ${root}, sub-retailers included` : `all of ${root}'s own locations`) : `all of ${n}`);
      else if (m.locations.length) parts.push(`${m.locations.length} location${m.locations.length === 1 ? '' : 's'} of ${n} (${m.locations.map(l => l.name).join(', ')})`);
      else parts.push(`${n}: no access — nothing picked`);
    }
    return parts.join(' · ');
  };
  const roleOf = (rows: AccessMembership[]) => rows.some(m => m.role === 'manager') ? 'manager' : 'viewer';

  return (
    <>
      <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2"><ShieldCheck size={18} /> Access for {name || '…'}</h5>
                {data && <div className="text-muted small">{data.account.email}</div>}
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={busy} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2"><AlertTriangle size={14} /> {error}</div>}
              {!data ? (
                <div className="text-center py-4"><Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} /></div>
              ) : (
                <>
                  <div className="mb-3" style={{ maxWidth: 420 }}>
                    <label htmlFor="acc-job-title" className="form-label small fw-semibold mb-1">Job title</label>
                    <div className="input-group input-group-sm">
                      <input id="acc-job-title" className="form-control" maxLength={100} value={jobTitle} disabled={busy} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Store Associate" />
                      <button type="button" className="btn btn-outline-primary" disabled={busy || jobTitle.trim() === (data.account.jobTitle ?? '')}
                        onClick={async () => { setBusy(true); setError(null); try { setData(await setAccountProfile(userId, jobTitle.trim())); onChanged?.(); } catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); } finally { setBusy(false); } }}>Save</button>
                    </div>
                    <div className="form-text">Shown on estimates they prepare for customers: &ldquo;Phil C. - Store Associate, Swanson&apos;s Home Hardware&rdquo;.</div>
                  </div>

                  {data.unplaced.length > 0 && (
                    <div className="alert alert-warning py-2 small">
                      Waiting with no retailer decided yet: {data.unplaced.map(u => `${u.source}${u.sfAccountName ? ` (Salesforce: ${u.sfAccountName})` : u.matchedDomain ? ` (@${u.matchedDomain})` : ''}`).join(', ')}.
                      Adding them to a retailer below places them and clears this.
                    </div>
                  )}

                  <div className="small fw-semibold mb-1">Retailers</div>
                  {trees.size === 0 && <div className="text-muted small mb-3">No retailer access yet.</div>}
                  {Array.from(trees.entries()).map(([rootId, rows]) => (
                    <div key={rootId} className={`border rounded-3 p-3 mb-2 d-flex align-items-start justify-content-between gap-3 ${rows.some(m => m.status !== 'approved') ? 'border-warning' : ''}`}>
                      <div>
                        <div className="fw-semibold">{byId.get(rootId)?.name ?? `#${rootId}`} <span className="badge text-bg-light border ms-1">{ACCESS_ROLE_LABEL[roleOf(rows)]}</span></div>
                        <div className="small text-muted">{describe(rows, rootId)}</div>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 flex-shrink-0" disabled={busy} onClick={() => setEditTree(rootId)}><Pencil size={13} /> Edit</button>
                    </div>
                  ))}

                  <div className="d-flex align-items-center gap-2 mt-3">
                    <select className="form-select form-select-sm" style={{ maxWidth: 360 }} value={addId} disabled={busy} onChange={e => setAddId(e.target.value)} aria-label="Add to a retailer">
                      <option value="">Add to a retailer…</option>
                      {data.retailers.filter(r => r.parentRetailerId === null && !trees.has(r.id)).map(r => (
                        <option key={r.id} value={r.id}>{r.name}{r.isActive ? '' : ' (inactive)'}</option>
                      ))}
                    </select>
                    <button type="button" className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1" disabled={busy || !addId} onClick={() => { setEditTree(Number(addId)); setAddId(''); }}>
                      <Plus size={14} /> Choose access
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer bg-light">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Close</button>
            </div>
          </div>
        </div>
      </div>
      {editTree !== null && (
        <AccessEditorModal userId={userId} retailerId={editTree} onClose={() => setEditTree(null)} onSaved={() => { void load(); onChanged?.(); }} />
      )}
    </>
  );
};

export default AccountAccessModal;
